<template>
  <div>
    <section class="relative mt-[100px] sm:mt-[75px] md:mt-[80px] lg:mt-[102px]">
      <div class="w-full h-5 bg-graies-1"></div>
      <div class="relative">
        <img :src="randBg.img" class="md:absolute inset-0 w-full h-full object-cover object-left-top md:block hidden" />
        <img :src="randBg.imgm" class="w-full md:hidden object-cover object-center" />

        <div class="container pb-14 md:pt-20 md:pb-24 lg:pt-28 lg:pb-[132px] relative -mt-12 md:mt-0">
          <h1
            class="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[115px] xl:leading-[115px] font-trajan text-red-1 md:text-white mb-1.5 md:mb-1"
          >
            {{ $t('Find Hope') }}
          </h1>
          <p
            v-if="route.query.timeout"
            class="text-xl text-red-1 md:text-white drop-shadow-2xl font-proxima md:max-w-[612px] mb-8 md:mb-[26px]"
          >
            {{
              $t(
                'It appears that your previous application session expired. Please answer the questions below and on the following screen and you will be able to complete your existing application or begin another.'
              )
            }}
          </p>
          <p v-else class="text-xl text-dark-1 md:text-white font-proxima md:max-w-[612px] mb-8 md:mb-[26px]">
            {{
              $t(
                `Did you know that one in four Americans live paycheck to paycheck with no savings to protect them from a financial crisis?`
              )
            }}
            <br />
            <br />
            {{
              $t(
                `If you are experiencing a financial challenge, The Salvation Army can help. Apply for assistance today by entering your home zip/postal code.`
              )
            }}
          </p>

          <form
            class="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4"
            @submit.prevent="onSubmit"
          >
            <input
              id="zipInput"
              type="tel"
              maxlength="5"
              minlength="5"
              pattern="^[0-9]{5}?$"
              required
              :data-val-regex="$t(`Please enter a valid 5 digit US Zip/Postal Code`)"
              name="zipCode"
              class="input-text text-center py-3.5 md:text-left md:max-w-[220px] placeholder-gray-300 placeholder:italic"
              :placeholder="$t(`Enter Zip`)"
            />
            <button :disabled="loading || loadingZip || restrictionLoading" class="btn-primary">
              <span v-if="loading || loadingZip || restrictionLoading">{{ $t(`Loading ...`) }}</span>
              <span v-else>{{ $t(`Search`) }}</span>
            </button>
          </form>
        </div>
      </div>
    </section>
    <section class="relative pt-[67px] pb-[125px] md:pb-[106px]">
      <div class="container">
        <SvgEmptyLogo />

        <h1 class="text-3xl md:text-[40px] text-red-1 text-center font-trajan mb-4 sm:mb-6 md:mb-8 lg:mb-10">
          {{ $t(`The Salvation Army can help`) }}
        </h1>

        <div class="grid lg:grid-cols-2 gap-6">
          <div>
            <p class="text-xl font-proxima text-black">
              {{
                $t(
                  `We know that you are visiting this site today because either you or someone you know is experiencing financial challenges and needs assistance.`
                )
              }}
              <br />
              <br />
              {{
                $t(
                  `By entering your home address Zip/Postal Code, we will match you to the appropriate Salvation Army location, and you can begin the confidential online application process for assistance.`
                )
              }}

              <br />
              <br />
              {{
                $t(
                  `We value your privacy and your time. We require only the information necessary to help us understand your needs and how we can best serve you. A case manager may contact you during the process for additional information necessary to complete your case.`
                )
              }}
            </p>
          </div>
          <div>
            <p class="text-xl font-proxima text-black">
              {{
                $t(
                  `Your application will be reviewed, and a final determination of how we can assist you will be communicated to you as quickly as possible.`
                )
              }}
              <br />
              <br />
              {{
                $t(
                  `If assistance is approved, you will be notified by email. If you have requested assistance with rent, mortgage or utilities, notification of this approval will also be sent to the utility company or housing contact to secure your account while the payment is processed.`
                )
              }}

              <br />
              <br />

              {{
                $t(
                  `Thank you for visiting SAHelp. We hope that this information and the application process will be helpful to you.`
                )
              }}
            </p>
          </div>
        </div>
      </div>
      <auth-redirect />
    </section>
    <HomeInactiveSa :modalOpen="modalOpen" @update:modalOpen="modalOpen = false" message="" @update:continue="continueAr" />
  </div>
</template>

<script setup lang="ts">
import { useServiceAreaStore } from '~/composables/stores/service-area';
import { clearUserSession } from '~/composables/utilities/helpers';

import img1m from '~/assets/images/bg-1-m.webp';
import img1 from '~/assets/images/bg-1.webp';
import img2m from '~/assets/images/bg-2-m.webp';
import img2 from '~/assets/images/bg-2.webp';
import img3m from '~/assets/images/bg-3-m.webp';
import img3 from '~/assets/images/bg-3.webp';
import img4m from '~/assets/images/bg-4-m.webp';
import img4 from '~/assets/images/bg-4.webp';
import { ServiceAreaRestriction } from '~/composables/queries/service-areas';
import { ServiceArea } from '~/composables/types/ServiceArea';
import { storeToRefs } from 'pinia';

const { t } = useI18n();

const languageStore = useLanguageStore();
const { availableLanguages, languageOptions } = storeToRefs(languageStore);
languageOptions.value = availableLanguages.value;

clearUserSession();

const route = useRoute();
const router = useRouter();
const useServiceArea = useServiceAreaStore();
const useApplicant = useApplicantStore();
onMounted(() => {
  useApplicant.resetAutoCorrect();
});
const arrBg = [
  { img: img1, imgm: img1m },
  { img: img2, imgm: img2m },
  { img: img3, imgm: img3m },
  { img: img4, imgm: img4m },
];
const randBg = ref(arrBg[Math.floor(Math.random() * 4)]);

const variables = ref({ zipCode: '', date: new Date().toISOString() });

const modalOpen = ref(false);

const { load, result, loading, onResult } = CheckZipcode({ fetchPolicy: 'network-only' }, variables);
const {
  load: refetchGetData,
  loading: loadingZip,
  result: zipResult,
  onResult: onResultZip,
} = GetZipcodeData({ fetchPolicy: 'network-only' }, variables);

const options = reactive({
  fetchPolicy: 'no-cache',
});
const { mutate: restrictionMutate, loading: restrictionLoading, onDone } = ServiceAreaRestriction(options);
const onSubmit = async (formData: any) => {
  try {
    clearUserSession();
    variables.value.zipCode = formData.target[0].value;
    variables.value.date = new Date().toISOString();
    const found = await checkLegacyZip(variables.value.zipCode);
    if (!found) {
      load();
    }
  } catch (e) {
    toastError(t('Zip/Postal Code'), t('An error has occurred - please try again later.'));
  }
};
const continueAr = () => {
  refetchGetData();
};
onResult((result) => {
  if (result?.data?.tbl_service_areas?.length) {
    useServiceArea.setListServiceAreas(result?.data?.tbl_service_areas);
    refetchGetData();
    // if (result?.data?.tbl_service_areas.filter((x: any) => x?.active === true).length === 0) {
    //   modalOpen.value = true;
    //   return;
    // } else {
    //   restrictionMutate({
    //     zipcode: variables.value.zipCode,
    //   });
    // }
  } else {
    toastError(
      t('Zip/Postal Code'),
      t(
        `This zip/postal code is not currently being served.  Please <a href='https://www.salvationarmyusa.org/usn/location-search/' class='underline'>CLICK HERE</a> to contact your local Salvation Army for more information on how to apply for assistance.`
      )
    );
  }
});

onDone((result) => {
  const listServiceAreas = JSON.parse(JSON.stringify(useServiceArea.listServiceAreas));
  const newList = listServiceAreas.map((x: ServiceArea) => {
    const sa = result?.data?.checkSaRestriction?.data?.find((_) => _.id === x.id);
    x.restricted = !sa?.canAdd;
    return x;
  });

  useServiceArea.setListServiceAreas(newList);
  if (newList.filter((x: any) => x?.restricted === true).length > 0) {
    modalOpen.value = true;
    return;
  } else {
    // get the zipcode data for the chosen service area
    refetchGetData();
  }
});

onResultZip(async (result) => {
  if (result.data?.getZipcodeData?.success) {
    useServiceArea.setZipcodeData(result.data.getZipcodeData.data);

    await router.push('/home/welcome');
    return;
  }
});

const config = useRuntimeConfig();
const checkLegacyZip = async (zipCode: string) => {
  const legacyZips = config.public.legacyRedirectZips ?? [];
  if (legacyZips.includes(zipCode)) {
    // redirect to legacy site
    window.location.href = `${config.public.legacyRedirectUrl}?z=${zipCode}`;
    return true;
  }
  return false;
};

const applicantStore = useApplicantStore();
applicantStore.clearAutoCorrectedApplicant();

const onSubmitTo = () => {
  console.log('submit');
}
</script>
